import { CAL_MULT, FAST_MATRIX, INTRO_MULT } from '@src/components/sco/Advisors/data/compCalculator'
import {
  FAST_CLIFF_MONTHS_OPTIONS,
  FAST_GOVERNING_LAW_INCORPORATION,
  FAST_GOVERNING_LAW_OPTIONS,
  FAST_GOVERNING_LAW_RECOMMENDED,
  FAST_HOURS_PER_MONTH_DEFAULT,
  FAST_HOURS_PER_MONTH_OPTIONS,
  FAST_SECURITY_TYPE_BLURBS,
  FAST_SECURITY_TYPE_NSO,
  FAST_SECURITY_TYPES,
  FAST_STAGE_BLURBS,
  FAST_TERM_MONTHS_OPTIONS,
  FAST_TIER_BLURBS,
  FAST_VESTING_MONTHS_OPTIONS,
  normalizeFastSecurityTypeKey,
  parseFastLegalTerms,
} from '@src/components/sco/Advisors/data/fastAgreement'
import { SEAT_CATEGORIES } from '@src/components/sco/Advisors/data/seatCategories'
import {
  AGREEMENT_CLIFF_MONTHS_DEFAULT,
  AGREEMENT_TERM_MONTHS_DEFAULT,
  AGREEMENT_VESTING_MONTHS_DEFAULT,
  CALIBER_KEY_TO_LABEL,
  COMPANY_STAGE_KEY_TO_LABEL,
  CONTRIBUTION_TIER_KEY_TO_LABEL,
  WARM_INTRO_KEY_TO_LABEL,
} from '@src/components/sco/Advisors/utils/agreementKeys'

export const FAST_INITIATING_PARTY_FOUNDER = 'founder'
export const FAST_INITIATING_PARTY_ADVISOR = 'advisor'

export type FastInitiatingPartyKey =
  typeof FAST_INITIATING_PARTY_FOUNDER | typeof FAST_INITIATING_PARTY_ADVISOR

export type FastAgreementDraftForm = {
  initiatingPartyKey: FastInitiatingPartyKey
  founderName: string
  founderEmail: string
  /** Shown name; equals the matched company's name unless the user typed a custom one. */
  founderCompanyName: string
  advisorName: string
  advisorEntityName: string
  advisorEmail: string
  advisorTitle: string
  companyStageKey: string
  contributionTierKey: string
  equityPercent: string
  vestingMonths: string
  cliffMonths: string
  hoursPerMonth: string
  termMonths: string
  startDate: string
  governingLaw: string
  securityTypeKey: string
  additionalTerms: string
}

export const normalizeInitiatingPartyKey = (value?: string | null): FastInitiatingPartyKey =>
  value === FAST_INITIATING_PARTY_ADVISOR ? FAST_INITIATING_PARTY_ADVISOR : FAST_INITIATING_PARTY_FOUNDER

const pad2 = (n: number) => String(n).padStart(2, '0')

/** DATEONLY values may arrive as YYYY-MM-DD or an ISO datetime. */
export const toDateOnly = (value?: string | null): string => {
  if (!value) {
    return ''
  }
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(value.trim())
  return match ? match[1] : ''
}

/** Effective date default: first calendar day of next month, local. */
export const defaultAgreementStartDate = (now = new Date()): string => {
  const year = now.getMonth() === 11 ? now.getFullYear() + 1 : now.getFullYear()
  const month = now.getMonth() === 11 ? 1 : now.getMonth() + 2
  return `${year}-${pad2(month)}-01`
}

export const formatDateOnlyLabel = (value?: string | null): string => {
  const ymd = toDateOnly(value)
  if (!ymd) {
    return ''
  }
  const [year, month, day] = ymd.split('-').map(Number)
  if (!year || !month || !day) {
    return ymd
  }
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export const formatEquityInput = (n: number): string => {
  const rounded = Math.round(n * 10000) / 10000
  return String(rounded)
}

export const emptyFastAgreementForm = (now = new Date()): FastAgreementDraftForm => ({
  initiatingPartyKey: FAST_INITIATING_PARTY_FOUNDER,
  founderName: '',
  founderEmail: '',
  founderCompanyName: '',
  advisorName: '',
  advisorEntityName: '',
  advisorEmail: '',
  advisorTitle: '',
  companyStageKey: '',
  contributionTierKey: '',
  equityPercent: '',
  vestingMonths: String(AGREEMENT_VESTING_MONTHS_DEFAULT),
  cliffMonths: String(AGREEMENT_CLIFF_MONTHS_DEFAULT),
  hoursPerMonth: String(FAST_HOURS_PER_MONTH_DEFAULT),
  termMonths: String(AGREEMENT_TERM_MONTHS_DEFAULT),
  startDate: defaultAgreementStartDate(now),
  governingLaw: '',
  securityTypeKey: FAST_SECURITY_TYPE_NSO,
  additionalTerms: '',
})

type AgreementDraftSource = {
  initiatingPartyKey?: string | null
  founderName?: string | null
  founderEmail?: string | null
  founderCompanyName?: string | null
  company?: { name?: string | null } | null
  advisorName?: string | null
  advisorEntityName?: string | null
  advisorEmail?: string | null
  advisorTitle?: string | null
  companyStageKey?: string | null
  contributionTierKey?: string | null
  equityPercent?: number | null
  vestingMonths?: number | null
  cliffMonths?: number | null
  hoursPerMonth?: number | null
  termMonths?: number | null
  startDate?: string | null
  legalTermsAsJson?: unknown
  advisorUser?: { fullName?: string | null; email?: string | null } | null
  founderUser?: { fullName?: string | null; displayName?: string | null; email?: string | null } | null
  engagement?: { category?: { title?: string | null } | null } | null
}

export const formFromAgreement = (agreement: AgreementDraftSource): FastAgreementDraftForm => {
  const initiatingPartyKey = normalizeInitiatingPartyKey(agreement.initiatingPartyKey)
  const advisorName = agreement.advisorName || agreement.advisorUser?.fullName || ''
  const advisorEmail = agreement.advisorEmail || agreement.advisorUser?.email || ''
  const founderName =
    agreement.founderName || agreement.founderUser?.fullName || agreement.founderUser?.displayName || ''
  const founderEmail = agreement.founderEmail || agreement.founderUser?.email || ''
  const legal = parseFastLegalTerms(agreement.legalTermsAsJson)
  return {
    initiatingPartyKey,
    founderName,
    founderEmail,
    founderCompanyName: agreement.founderCompanyName || agreement.company?.name || '',
    advisorName,
    advisorEntityName: agreement.advisorEntityName || '',
    advisorEmail,
    advisorTitle: agreement.advisorTitle || agreement.engagement?.category?.title || '',
    companyStageKey: agreement.companyStageKey || '',
    contributionTierKey: agreement.contributionTierKey || '',
    equityPercent:
      agreement.equityPercent != null && !Number.isNaN(Number(agreement.equityPercent))
        ? String(agreement.equityPercent)
        : '',
    vestingMonths:
      agreement.vestingMonths != null
        ? String(agreement.vestingMonths)
        : String(AGREEMENT_VESTING_MONTHS_DEFAULT),
    cliffMonths:
      agreement.cliffMonths != null ? String(agreement.cliffMonths) : String(AGREEMENT_CLIFF_MONTHS_DEFAULT),
    hoursPerMonth:
      agreement.hoursPerMonth != null
        ? String(agreement.hoursPerMonth)
        : String(FAST_HOURS_PER_MONTH_DEFAULT),
    termMonths:
      agreement.termMonths != null ? String(agreement.termMonths) : String(AGREEMENT_TERM_MONTHS_DEFAULT),
    startDate: toDateOnly(agreement.startDate) || defaultAgreementStartDate(),
    governingLaw: legal.governingLaw || '',
    securityTypeKey: normalizeFastSecurityTypeKey(legal.securityType),
    additionalTerms: legal.additionalTerms || '',
  }
}

export const FAST_AREA_OF_ADVICE_OPTIONS = SEAT_CATEGORIES.map(category => category.name)

export type SelectItem = { value: string; label: string }

export type ChoiceItem = { value: string; label: string; description?: string }

export const withCurrentOption = (items: SelectItem[], current?: string | null): SelectItem[] => {
  const trimmed = current?.trim()
  if (!trimmed || items.some(item => item.value === trimmed)) {
    return items
  }
  return [{ value: trimmed, label: trimmed }, ...items]
}

const monthsLabel = (n: number, noneLabel = 'None') => (n === 0 ? noneLabel : `${n} months`)

export const hoursPerMonthSelectItems = (current?: string | null): SelectItem[] =>
  withCurrentOption(
    FAST_HOURS_PER_MONTH_OPTIONS.map(n => ({ value: String(n), label: `${n} hours` })),
    current
  )

export const vestingMonthsSelectItems = (current?: string | null): SelectItem[] =>
  withCurrentOption(
    FAST_VESTING_MONTHS_OPTIONS.map(n => ({
      value: String(n),
      label: n === AGREEMENT_VESTING_MONTHS_DEFAULT ? `${n} months (FAST)` : monthsLabel(n),
    })),
    current
  )

export const cliffMonthsSelectItems = (current?: string | null): SelectItem[] =>
  withCurrentOption(
    FAST_CLIFF_MONTHS_OPTIONS.map(n => ({
      value: String(n),
      label: n === AGREEMENT_CLIFF_MONTHS_DEFAULT ? `${monthsLabel(n)} (FAST)` : monthsLabel(n),
    })),
    current
  )

export const termMonthsSelectItems = (current?: string | null): SelectItem[] =>
  withCurrentOption(
    FAST_TERM_MONTHS_OPTIONS.map(n => ({ value: String(n), label: monthsLabel(n) })),
    current
  )

export const areaOfAdviceSelectItems = (current?: string | null): SelectItem[] =>
  withCurrentOption(
    FAST_AREA_OF_ADVICE_OPTIONS.map(name => ({ value: name, label: name })),
    current
  )

export const governingLawSelectItems = (current?: string | null): SelectItem[] =>
  withCurrentOption(
    FAST_GOVERNING_LAW_OPTIONS.map(name =>
      name === FAST_GOVERNING_LAW_RECOMMENDED
        ? { value: name, label: 'Delaware (recommended)' }
        : name === FAST_GOVERNING_LAW_INCORPORATION
          ? { value: name, label: "Company's jurisdiction of incorporation" }
          : { value: name, label: name }
    ),
    current
  )

export const securityTypeSelectItems = (): SelectItem[] =>
  FAST_SECURITY_TYPES.map(({ key, title }) => ({
    value: key,
    label: key === FAST_SECURITY_TYPE_NSO ? `${title} (FAST)` : title,
  }))

const withCurrentChoice = (items: ChoiceItem[], current?: string | null): ChoiceItem[] => {
  const trimmed = current?.trim()
  if (!trimmed || items.some(item => item.value === trimmed)) {
    return items
  }
  return [{ value: trimmed, label: trimmed }, ...items]
}

export const areaOfAdviceChoiceItems = (current?: string | null): ChoiceItem[] =>
  withCurrentChoice(
    SEAT_CATEGORIES.map(category => ({
      value: category.name,
      label: category.name,
      description: category.role,
    })),
    current
  )

export const serviceLevelChoiceItems = (): ChoiceItem[] =>
  [
    { key: 'standard', label: 'Standard' },
    { key: 'strategic', label: 'Strategic' },
    { key: 'expert', label: 'Expert' },
  ].map(item => ({
    value: item.key,
    label: item.label,
    description: FAST_TIER_BLURBS[item.key],
  }))

export const companyStageChoiceItems = (): ChoiceItem[] =>
  [
    { key: 'idea', label: 'Pre-seed' },
    { key: 'startup', label: 'Seed' },
    { key: 'growth', label: 'Series A' },
  ].map(item => ({
    value: item.key,
    label: item.label,
    description: FAST_STAGE_BLURBS[item.key],
  }))

export const securityTypeChoiceItems = (): ChoiceItem[] =>
  FAST_SECURITY_TYPES.map(({ key, title }) => ({
    value: key,
    label: key === FAST_SECURITY_TYPE_NSO ? `${title} (FAST)` : title,
    description: FAST_SECURITY_TYPE_BLURBS[key],
  }))

export const hoursPerMonthChoiceItems = (current?: string | null): ChoiceItem[] =>
  withCurrentChoice(
    FAST_HOURS_PER_MONTH_OPTIONS.map(n => ({
      value: String(n),
      label: `${n} hours`,
    })),
    current
  )

export const termMonthsChoiceItems = (current?: string | null): ChoiceItem[] =>
  withCurrentChoice(
    FAST_TERM_MONTHS_OPTIONS.map(n => ({
      value: String(n),
      label: n === AGREEMENT_TERM_MONTHS_DEFAULT ? `${monthsLabel(n)} (FAST)` : monthsLabel(n),
    })),
    current
  )

export const vestingMonthsChoiceItems = (current?: string | null): ChoiceItem[] =>
  withCurrentChoice(
    FAST_VESTING_MONTHS_OPTIONS.map(n => ({
      value: String(n),
      label: n === AGREEMENT_VESTING_MONTHS_DEFAULT ? `${n} months (FAST)` : monthsLabel(n),
    })),
    current
  )

export const cliffMonthsChoiceItems = (current?: string | null): ChoiceItem[] =>
  withCurrentChoice(
    FAST_CLIFF_MONTHS_OPTIONS.map(n => ({
      value: String(n),
      label: n === AGREEMENT_CLIFF_MONTHS_DEFAULT ? `${monthsLabel(n)} (FAST)` : monthsLabel(n),
    })),
    current
  )

export const recommendedEquityPercent = ({
  companyStageKey,
  contributionTierKey,
  caliberKey,
  warmIntroKey,
}: {
  companyStageKey?: string | null
  contributionTierKey?: string | null
  caliberKey?: string | null
  warmIntroKey?: string | null
}): number | null => {
  const stageLabel = companyStageKey ? COMPANY_STAGE_KEY_TO_LABEL[companyStageKey] : undefined
  const tierLabel = contributionTierKey ? CONTRIBUTION_TIER_KEY_TO_LABEL[contributionTierKey] : undefined
  const caliberLabel = CALIBER_KEY_TO_LABEL[caliberKey || 'executive'] || 'Executive'
  const introLabel = WARM_INTRO_KEY_TO_LABEL[warmIntroKey || 'none'] || 'None'
  const base = stageLabel && tierLabel ? FAST_MATRIX[stageLabel]?.[tierLabel] : undefined
  if (base == null) {
    return null
  }
  const calMult = CAL_MULT[caliberLabel]
  const introMult = INTRO_MULT[introLabel]
  if (calMult == null || introMult == null) {
    return null
  }
  return base * calMult * introMult
}

const FAST_DEFAULT_STAGE_KEY = 'idea'
const FAST_DEFAULT_TIER_KEY = 'strategic'

export const applyStandardFastTerms = (
  form: FastAgreementDraftForm,
  agreement?: {
    companyStageKey?: string | null
    contributionTierKey?: string | null
    caliberKey?: string | null
    warmIntroKey?: string | null
    recommendedEquity?: unknown
  } | null,
  now = new Date()
): FastAgreementDraftForm => {
  const companyStageKey = form.companyStageKey || agreement?.companyStageKey || FAST_DEFAULT_STAGE_KEY
  const contributionTierKey =
    form.contributionTierKey || agreement?.contributionTierKey || FAST_DEFAULT_TIER_KEY
  const fromMatrix = recommendedEquityPercent({
    companyStageKey,
    contributionTierKey,
    caliberKey: agreement?.caliberKey,
    warmIntroKey: agreement?.warmIntroKey,
  })
  const recommended = agreement?.recommendedEquity
  const fromServer =
    recommended && typeof recommended === 'object' && recommended !== null && 'equityPercent' in recommended
      ? Number((recommended as { equityPercent?: number }).equityPercent)
      : null
  const equity = fromMatrix ?? (fromServer != null && Number.isFinite(fromServer) ? fromServer : null)

  return {
    ...form,
    companyStageKey,
    contributionTierKey,
    equityPercent: equity != null ? formatEquityInput(equity) : form.equityPercent,
    vestingMonths: String(AGREEMENT_VESTING_MONTHS_DEFAULT),
    cliffMonths: String(AGREEMENT_CLIFF_MONTHS_DEFAULT),
    termMonths: String(AGREEMENT_TERM_MONTHS_DEFAULT),
    hoursPerMonth: String(FAST_HOURS_PER_MONTH_DEFAULT),
    startDate: toDateOnly(form.startDate) || defaultAgreementStartDate(now),
    governingLaw: form.governingLaw.trim() || FAST_GOVERNING_LAW_RECOMMENDED,
    securityTypeKey: normalizeFastSecurityTypeKey(form.securityTypeKey),
  }
}

export const isAdvisorInitiated = (value?: string | null): boolean =>
  normalizeInitiatingPartyKey(value) === FAST_INITIATING_PARTY_ADVISOR

export const recipientSectionComplete = (form: FastAgreementDraftForm): boolean =>
  Boolean(
    form.founderName.trim() && form.founderEmail.trim() && form.advisorName.trim() && form.advisorEmail.trim()
  )

export const engagementSectionComplete = (form: FastAgreementDraftForm): boolean =>
  Boolean(form.advisorTitle.trim() && form.contributionTierKey)

export const companySecuritySectionComplete = (form: FastAgreementDraftForm): boolean =>
  Boolean(form.companyStageKey && form.securityTypeKey)

export const timingSectionComplete = (form: FastAgreementDraftForm): boolean =>
  Boolean(form.startDate && form.governingLaw.trim() && form.hoursPerMonth && form.termMonths)

export const termsSectionComplete = (form: FastAgreementDraftForm): boolean =>
  engagementSectionComplete(form) && companySecuritySectionComplete(form) && timingSectionComplete(form)

export const equitySectionComplete = (form: FastAgreementDraftForm): boolean =>
  Boolean(form.equityPercent.trim() && form.vestingMonths && form.cliffMonths)

/** Custom company name to persist; null when blank or just the matched company's own name. */
export const customFounderCompanyName = (value: string, matchedCompanyName?: string | null): string | null => {
  const typed = value.trim()
  if (!typed) {
    return null
  }
  return typed.toLowerCase() === (matchedCompanyName?.trim().toLowerCase() || '') ? null : typed
}

export const upsertPartyFieldsFromForm = (form: FastAgreementDraftForm, matchedCompanyName?: string | null) => ({
  initiatingPartyKey: normalizeInitiatingPartyKey(form.initiatingPartyKey),
  founderName: form.founderName.trim() || null,
  founderEmail: form.founderEmail.trim() || null,
  founderCompanyName: customFounderCompanyName(form.founderCompanyName, matchedCompanyName),
  advisorName: form.advisorName.trim() || null,
  advisorEmail: form.advisorEmail.trim() || null,
  advisorEntityName: form.advisorEntityName.trim() || null,
})
